import { masterMark } from '@deltasuite/ui-tokens';

// Renders when a production build was deployed without VITE_SUPABASE_URL /
// VITE_SUPABASE_ANON_KEY in the build environment. We REFUSE to render the
// app — silently falling through to a no-auth path is the worst possible
// failure mode on a prod deploy. This panel makes the misconfiguration
// visible at the page level so a tester without DevTools can still see
// exactly what env is missing.
//
// Importantly: do NOT print the actual values, only truthy/falsy. The anon
// key is technically public but logging it to the page is still ugly.
export function AuthMisconfigured() {
  // `?.trim() || ''` — `||` (not `??`) so empty strings exposed by Cloudflare
  // Pages count as missing. See config.ts comment for context.
  const checks: Array<{ name: string; present: boolean; required: boolean }> = [
    {
      name: 'VITE_SUPABASE_URL',
      required: true,
      present: (import.meta.env.VITE_SUPABASE_URL?.trim() || '').length > 0,
    },
    {
      name: 'VITE_SUPABASE_ANON_KEY',
      required: true,
      present: (import.meta.env.VITE_SUPABASE_ANON_KEY?.trim() || '').length > 0,
    },
    {
      // Accept either name; api/client.ts checks both. Display the
      // canonical one but the panel passes if either is set.
      name: 'VITE_DIAL_API_URL (or VITE_API_BASE_URL)',
      required: true,
      present:
        (import.meta.env.VITE_DIAL_API_URL?.trim() || '').length > 0 ||
        (import.meta.env.VITE_API_BASE_URL?.trim() || '').length > 0,
    },
    {
      name: 'VITE_AUTH_REDIRECT_URI',
      required: false,
      present: (import.meta.env.VITE_AUTH_REDIRECT_URI?.trim() || '').length > 0,
    },
  ];

  return (
    <main className="flex min-h-full items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-graphite-700">
          <span className="font-sans text-2xl leading-none text-signal-amber" aria-hidden>
            {masterMark}
          </span>
          <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
            DeltaDial
          </span>
        </div>

        <h1 className="mt-10 font-sans text-3xl font-semibold tracking-tight text-graphite-900">
          Auth not configured.
        </h1>
        <p className="mt-3 font-sans text-sm text-graphite-700">
          This build was deployed without the Supabase env it needs. Sign-in is blocked until the
          missing variables land in the Cloudflare Pages build environment and the project is
          redeployed.
        </p>

        <dl className="mt-6 space-y-1 rounded-md border border-graphite-100 bg-surface-sunk p-4 font-mono text-xs">
          {checks.map((c) => {
            const status = c.present ? 'set' : c.required ? 'MISSING' : 'optional, defaulted';
            const tone = !c.present && c.required ? 'text-signal-amber' : 'text-graphite-900';
            return (
              <div key={c.name} className="flex items-center justify-between">
                <dt className="text-graphite-700">{c.name}</dt>
                <dd className={tone}>{status}</dd>
              </div>
            );
          })}
        </dl>

        <p className="mt-6 font-sans text-xs text-graphite-500">
          Cloudflare Pages → Settings → Environment Variables. Set on both Production and Preview,
          then trigger a redeploy.
        </p>
      </div>
    </main>
  );
}
